import { useState, useEffect } from 'react';
import {
  Stack,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PublicIcon from '@mui/icons-material/Public';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import moment from 'moment-timezone';
import { useCalendarEventStore } from '../../stores/root_store';
import type { CalendarEvent, CalendarListItem } from '../../types/calendar';

const FAKE_PREFIX = 'FAKE://';

export interface RenderableEvent {
  id: string;
  calendar_id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  description?: string;
  location?: string;
  backgroundColor?: string;
  textColor?: string;
  colorId?: string;
  borderColor?: string;
  isUnsaved: boolean;
  editable: boolean;
  rrule?: string;
}

interface CalendarEventEditorProps {
  event: RenderableEvent;
  startStr: string;
  endStr: string;
  timezoneStr: string;
  associatedType: string;
  associatedUuid: string;
  calendarList: CalendarListItem[];
  onCancel: () => void;
  onSaved: (event: CalendarEvent | null) => void;
  onDeleted: () => void;
}

export default function CalendarEventEditor({
  event,
  startStr,
  endStr,
  timezoneStr,
  associatedType,
  associatedUuid,
  calendarList,
  onCancel,
  onSaved,
  onDeleted,
}: CalendarEventEditorProps) {
  const calendarEventStore = useCalendarEventStore();

  const [title, setTitle] = useState(event.title);
  const [stateId, setStateId] = useState('');
  const [startTime, setStartTime] = useState<moment.Moment>(moment(startStr));
  const [endTime, setEndTime] = useState<moment.Moment>(moment(endStr));
  const [location, setLocation] = useState(event.location || '');
  const [description, setDescription] = useState(event.description || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get default calendar (primary or first available)
  const defaultCalendarId =
    event.calendar_id ||
    calendarList.find((c) => c.primary)?.id ||
    calendarList[0]?.id ||
    '';
  const [calendarId, setCalendarId] = useState(defaultCalendarId);


  useEffect(() => {
    if (stateId !== event.id) {
      setStateId(event.id || '');
      setTitle(event.title || '');
      setDescription(event.description || '');
      setLocation(event.location || '');
    }
    setStartTime(moment(startStr));
    setEndTime(moment(endStr));
  }, [event, startStr, endStr, stateId]);

  useEffect(() => {
    if (!calendarId && calendarList.length > 0) {
      const primary = calendarList.find((c) => c.primary);
      setCalendarId(primary?.id || calendarList[0].id);
    }
  }, [calendarList, calendarId]);

  async function handleDelete() {

    setDeleting(true);
    try {
      const success = await calendarEventStore.deleteEvent(
        associatedType,
        associatedUuid,
        event.id,
        event.calendar_id
      );
      if (success) {
        onDeleted();
      }
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleSave() {
    const data: CalendarEvent = {
      id: event.id,
      calendar: { id: calendarId },
      summary: title,
      description: description,
      location: location,
      start: { dateTime: startTime.format() },
      end: { dateTime: endTime.format() },
    };

    setSaving(true);
    try {
      let result: CalendarEvent | null = null;

      if (data.id.startsWith(FAKE_PREFIX)) {
        result = await calendarEventStore.createEvent(
          associatedType,
          associatedUuid,
          data
        );
      } else {
        result = await calendarEventStore.updateEvent(
          associatedType,
          associatedUuid,
          data
        );
      }

      onSaved(result);
    } finally {
      setSaving(false);
    }
  }

  const selectedCalendar = calendarList.find((c) => c.id === calendarId);

  return (
    <Stack spacing={3} p={2}>
      {/* Date and Time Header */}
      <FormControl>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <EditCalendarIcon />
          <Typography variant="h6">
            {startTime.format('dddd, MMMM D, YYYY')}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <AccessTimeIcon fontSize="small" color="action" />
          <Chip
            label={startTime.tz(timezoneStr).format('h:mm A')}
            size="small"
            variant="outlined"
          />
          <ArrowForwardIcon fontSize="small" color="action" />
          <Chip
            label={endTime.tz(timezoneStr).format('h:mm A')}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <PublicIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {timezoneStr}
            </Typography>
          </Stack>

          {!event.id.startsWith(FAKE_PREFIX) && event.editable && (
            <IconButton
              size="small"
              color="error"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <DeleteOutlineIcon />
            </IconButton>
          )}
        </Stack>
      </FormControl>

      {/* Calendar Selector */}
      {calendarList.length > 0 && (
        <FormControl fullWidth size="small">
          <InputLabel>Calendar</InputLabel>
          <Select
            value={calendarId}
            label="Calendar"
            onChange={(e) => event.editable && setCalendarId(e.target.value)}
            disabled={!event.isUnsaved}
            renderValue={() => (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CalendarMonthIcon
                  sx={{ color: selectedCalendar?.backgroundColor, fontSize: 20 }}
                />
                <Typography>
                  {selectedCalendar?.summaryOverride || selectedCalendar?.summary}
                </Typography>
              </Stack>
            )}
          >
            {calendarList.map((cal) => (
              <MenuItem
                key={cal.id}
                value={cal.id}
                disabled={cal.access_role === 'reader'}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarMonthIcon
                    sx={{
                      color: cal.backgroundColor,
                      opacity: cal.access_role === 'reader' ? 0.5 : 1,
                    }}
                  />
                  <Typography
                    sx={{ opacity: cal.access_role === 'reader' ? 0.5 : 1 }}
                  >
                    {cal.summaryOverride || cal.summary}
                  </Typography>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Title */}
      <TextField
        label="Title"
        value={title}
        onChange={(e) => event.editable && setTitle(e.target.value)}
        fullWidth
        size="small"
        placeholder="Concise meeting title"
        disabled={!event.editable}
      />

      {/* Description */}
      <TextField
        label="Description"
        value={description}
        onChange={(e) => event.editable && setDescription(e.target.value)}
        fullWidth
        size="small"
        multiline
        rows={3}
        disabled={!event.editable}
      />

      {/* Location */}
      <TextField
        label="Location"
        value={location}
        onChange={(e) => event.editable && setLocation(e.target.value)}
        fullWidth
        size="small"
        placeholder="Address or meeting link"
        disabled={!event.editable}
      />

      {/* Actions */}
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button variant={event.editable ? 'text' : 'outlined'} onClick={onCancel}>
          Cancel
        </Button>
        {event.editable && (
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)}>
        <DialogTitle>Delete Event?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{event.title || 'this event'}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
